import React, { FC, useContext, useMemo, useState } from "react";
import clsx from "clsx";

import {
  Context,
  FETCH_ACTIVE_LEARNING_INQUIRY_EVALUATIONS_SUCCESS,
} from "../../../teacherStore";
import {
  IBreadcrumbsLink,
  IStudent,
  ActiveLearningMaterialCode,
} from "../../../state";
import { ACTIVE_LEARNING_MATERIAL_CODE } from "../../../constants/active_learning";
import DropdownMenu, {
  Option as DropdownOption,
} from "../../../atoms/DropdownMenu";
import TeacherHeader from "../../../organisms/teacher/TeacherHeader";
import { ReactComponent as ClassDataIcon } from "../../../assets/svg/class-data.svg";

import CsvDownloadButton from "./CsvDownloadButton";
import InquiryEvaluationTable from "./InquiryEvaluationTable";
import InquiryEvaluationCriteriaModal from "./InquiryEvaluationCriteriaModal";
import styles from "./ActiveLearningStudentList.module.scss";

type Props = {
  listStudents: IStudent[];
  calling: boolean;
  nameClass: string;
  links: IBreadcrumbsLink[];
  isSuccessCSVDownload: boolean;
  goBack: () => void;
  goToStudentResponse: (student: IStudent) => void;
  onDownloadInquiryEvaluationCSV: (
    material_code: ActiveLearningMaterialCode,
  ) => void;
};

enum SortValue {
  Kana = 0,
  TotalScoreDesc = 1,
}

enum RoundValue {
  First = 0,
  Second = 1,
}

const SORT_OPTIONS: DropdownOption[] = [
  { value: SortValue.Kana, label: "50音順" },
  { value: SortValue.TotalScoreDesc, label: "スコアが高い順" },
];

const ROUND_OPTIONS: DropdownOption[] = [
  {
    value: RoundValue.First,
    label: "1周目",
    data: ACTIVE_LEARNING_MATERIAL_CODE.FirstRound,
  },
  {
    value: RoundValue.Second,
    label: "2周目",
    data: ACTIVE_LEARNING_MATERIAL_CODE.SecondRound,
  },
];

export const ActiveLearningStudentList: FC<Props> = ({
  listStudents,
  calling,
  nameClass,
  links,
  isSuccessCSVDownload,
  goBack,
  goToStudentResponse,
  onDownloadInquiryEvaluationCSV,
}) => {
  const { state } = useContext(Context);
  const inquiryEvaluations = state.active_learning_inquiry_evaluations;
  const inquiryEvaluationsCSV = state.active_learning_inquiry_evaluations_csv;

  const [sortValue, setSortValue] = useState<SortValue>(SortValue.Kana);
  const [materialCode, setMaterialCode] = useState<ActiveLearningMaterialCode>(
    ACTIVE_LEARNING_MATERIAL_CODE.FirstRound,
  );
  const [
    isShowInquiryEvaluationCriteriaModal,
    setIsShowInquiryEvaluationCriteriaModal,
  ] = useState(false);

  const studentsById = useMemo(
    () => new Map(listStudents.map(student => [student.id, student])),
    [listStudents],
  );

  const inquiryEvaluationsByMaterialCode = useMemo(
    () =>
      inquiryEvaluations.map(studentEvaluation => {
        const evaluationForMaterialCode =
          studentEvaluation.data?.find(
            ({ evaluation }) =>
              evaluation.active_learning_material_code === materialCode,
          ) ?? null;

        return {
          ...studentEvaluation,
          data: evaluationForMaterialCode ? [evaluationForMaterialCode] : null,
        };
      }),
    [inquiryEvaluations, materialCode],
  );

  const sortedInquiryEvaluationsByKana = useMemo(
    () =>
      [...inquiryEvaluationsByMaterialCode].sort((a, b) =>
        `${a.student.last_name_kana}${a.student.first_name_kana}`.localeCompare(
          `${b.student.last_name_kana}${b.student.first_name_kana}`,
          "ja",
        ),
      ),
    [inquiryEvaluationsByMaterialCode],
  );

  const sortedInquiryEvaluationsByTotalScoreDesc = useMemo(
    () =>
      [...inquiryEvaluationsByMaterialCode].sort(
        (a, b) =>
          (b.data?.[0]?.evaluation.total_score ?? -1) -
          (a.data?.[0]?.evaluation.total_score ?? -1),
      ),
    [inquiryEvaluationsByMaterialCode],
  );

  const sortedInquiryEvaluations = useMemo(() => {
    switch (sortValue) {
      case SortValue.Kana:
        return sortedInquiryEvaluationsByKana;
      case SortValue.TotalScoreDesc:
        return sortedInquiryEvaluationsByTotalScoreDesc;
      default:
        return sortedInquiryEvaluationsByKana;
    }
  }, [
    sortValue,
    sortedInquiryEvaluationsByKana,
    sortedInquiryEvaluationsByTotalScoreDesc,
  ]);

  const isInquiryEvaluationsEmpty =
    state.lastAction === FETCH_ACTIVE_LEARNING_INQUIRY_EVALUATIONS_SUCCESS &&
    sortedInquiryEvaluations.length === 0;

  return (
    <>
      {isShowInquiryEvaluationCriteriaModal && (
        <InquiryEvaluationCriteriaModal
          onClose={() => setIsShowInquiryEvaluationCriteriaModal(false)}
        />
      )}
      <TeacherHeader links={links} />
      <div className={styles.studentList}>
        <div className={styles.btnBack} onClick={goBack}>
          くクラス選択に戻る
        </div>
        <div className="mt-3">
          <div className={clsx("mb-3", styles.header)}>
            <ClassDataIcon />
            <span>{nameClass}</span>
          </div>
        </div>
        <div className={styles.wrapper}>
          <div className={styles.intro}>
            <p className={styles.title}>
              「課題の設定」の質を高める指導例（生徒個別）
            </p>
            <p className={styles.desc}>
              探究の質を左右する「問い」に注目し、「課題の設定」をサポートします。
              <br />
              生徒の問いを3つの観点（内発性・思考の深さ・具体性）から評価し、指導例を提示します。
            </p>
            <p className={styles.note}>
              ※３つの観点に関する詳細は
              <span
                className={styles.link}
                onClick={() => setIsShowInquiryEvaluationCriteriaModal(true)}
              >
                こちら
              </span>
              <br />
              ※スコアと指導例は、生徒の回答記入、もしくは変更後約1時間ほどで更新されます。
              <br />
              ※生徒がLesson01〜03を回答すると、スコア・指導例が生成されます。
            </p>
          </div>
          {sortedInquiryEvaluations.length > 0 && (
            <div className={styles.menuBlock}>
              <DropdownMenu
                options={ROUND_OPTIONS}
                defaultValue={ROUND_OPTIONS[0]}
                onChange={(_, data) => setMaterialCode(data)}
              />
              <DropdownMenu
                options={SORT_OPTIONS}
                defaultValue={SORT_OPTIONS[0]}
                onChange={value => setSortValue(value)}
              />
              <CsvDownloadButton
                calling={calling}
                isSuccessCSVDownload={isSuccessCSVDownload}
                csvData={inquiryEvaluationsCSV}
                onDownload={() => onDownloadInquiryEvaluationCSV(materialCode)}
              />
            </div>
          )}
          <div className={styles.container}>
            {sortedInquiryEvaluations.length > 0 ? (
              sortedInquiryEvaluations.map(studentEvaluation => (
                <InquiryEvaluationTable
                  key={studentEvaluation.student.id}
                  studentEvaluation={studentEvaluation}
                  goToStudentResponse={() => {
                    const student = studentsById.get(
                      studentEvaluation.student.id,
                    );
                    if (student) goToStudentResponse(student);
                  }}
                />
              ))
            ) : isInquiryEvaluationsEmpty ? (
              <p className={styles.emptyMessage}>生徒データがありません。</p>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
};

export default ActiveLearningStudentList;
